<script lang="ts" setup>
import { RouterLink, useRoute } from 'vue-router'
import { computed } from 'vue'
import { BookOpen, LayoutDashboard, Library, Moon, Sun, User } from 'lucide-vue-next'
import { useAuth } from '@/modules/auth'
import { useTheme } from '@/infra/composables'

const auth = useAuth()
const theme = useTheme()
const route = useRoute()

const isActive = computed(() => ({
  dashboard: route.path === '/dashboard',
  readings: route.path.startsWith('/readings'),
  library: route.path === '/books',
  account: route.path === '/account',
  login: route.path === '/login',
}))
</script>

<template>
  <nav
    class="border-b sticky top-0 bg-background z-40"
    :class="{ 'hidden sm:block': auth.isAuthenticated }"
  >
    <div class="container mx-auto px-4 py-3">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <slot name="left-action" />

          <RouterLink
            v-if="!auth.isAuthenticated"
            to="/"
            data-nav-title
            class="text-lg text-foreground"
          >
            MyRead
          </RouterLink>

          <template v-if="auth.isAuthenticated">
            <RouterLink
              to="/dashboard"
              data-nav-dashboard
              class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
              :class="{
                'border-b-2 border-primary': isActive.dashboard,
                'nav-link-hover': !isActive.dashboard,
              }"
            >
              <LayoutDashboard class="h-4 w-4" />
              Dashboard
            </RouterLink>
            <RouterLink
              to="/readings"
              data-nav-readings
              class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
              :class="{
                'border-b-2 border-primary': isActive.readings,
                'nav-link-hover': !isActive.readings,
              }"
            >
              <BookOpen class="h-4 w-4" />
              Readings
            </RouterLink>
            <RouterLink
              to="/books?by_user=true"
              data-nav-library
              class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
              :class="{
                'border-b-2 border-primary': isActive.library,
                'nav-link-hover': !isActive.library,
              }"
            >
              <Library class="h-4 w-4" />
              Library
            </RouterLink>
          </template>
        </div>

        <div v-if="auth.isAuthenticated" class="flex items-center gap-3">
          <button
            data-theme-toggle
            class="p-2 rounded-md hover:bg-accent transition-colors"
            @click="theme.toggleTheme"
          >
            <Sun v-if="theme.isDark.value" class="h-4 w-4" />
            <Moon v-else class="h-4 w-4" />
          </button>
          <RouterLink
            to="/account"
            data-nav-account
            class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
            :class="{
              'border-b-2 border-primary': isActive.account,
              'nav-link-hover': !isActive.account,
            }"
          >
            <User class="h-4 w-4" />
            Account
          </RouterLink>
        </div>

        <div v-if="!auth.isAuthenticated" class="flex items-center gap-3">
          <button
            data-theme-toggle
            class="p-2 rounded-md hover:bg-accent transition-colors"
            @click="theme.toggleTheme"
          >
            <Sun v-if="theme.isDark.value" class="h-4 w-4" />
            <Moon v-else class="h-4 w-4" />
          </button>
          <RouterLink
            to="/login"
            data-nav-get-started
            class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
            :class="{
              'border-b-2 border-primary': isActive.login,
              'nav-link-hover': !isActive.login,
            }"
          >
            Get Started
          </RouterLink>
        </div>
      </div>
    </div>
  </nav>
</template>

<style scoped>
.nav-link-hover {
  position: relative;
}

.nav-link-hover::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background-color: hsl(var(--primary));
  transition: width 0.3s ease-in-out;
}

.nav-link-hover:hover::after {
  width: 100%;
}
</style>
